import { HeartHandshake, ListTree, Plus, X } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { apiErrorMessage } from '../api/client'
import { connectionsApi, guardianApi } from '../api/endpoints'
import { ChildActivityTrail } from '../components/ChildActivityTrail'
import { ProviderDecisionCard } from '../components/ProviderDecisionCard'
import { DateField } from '../components/DateField'
import { ErrorMessage } from '../components/ErrorMessage'
import { DOB_MAX, DOB_MIN, DOB_OPEN_AT } from '../utils/age'
import { isValidMobile, MOBILE_PATTERN } from '../utils/validation'
import type { ConnectionRequestResponse, GuardianLinkViewResponse } from '../types/api'

const RELATIONS = ['Mother', 'Father', 'Guardian', 'Other']

type OpenForm = 'link' | 'create' | null

export function GuardianPage() {
  const [links, setLinks] = useState<GuardianLinkViewResponse[]>([])
  const [open, setOpen] = useState<OpenForm>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  /** The child whose connection trail is open, if any. */
  const [trailFor, setTrailFor] = useState<string | null>(null)
  /** Connection requests involving a child of this parent, waiting on their answer. */
  const [connQueue, setConnQueue] = useState<ConnectionRequestResponse[]>([])

  const load = useCallback(() => {
    guardianApi
      .listMyLinks()
      .then(setLinks)
      .catch((err) => setError(apiErrorMessage(err, 'Could not load your children')))
      .finally(() => setLoading(false))
    // Separate call, and deliberately not fatal to the page: failing to load the approval queue
    // should not hide the children list as well.
    connectionsApi
      .guardianQueue()
      .then(setConnQueue)
      .catch((err) => console.error('[guardian] could not load the approval queue', err))
  }, [])

  useEffect(load, [load])

  // Only links where this participant is the guardian belong on this page.
  const mine = links.filter((l) => !!l.guardianParticipantId && l.status !== 'REVOKED')
  const waitingOnMe = links.filter((l) => l.awaitingMyApproval)

  async function respond(link: GuardianLinkViewResponse, accept: boolean) {
    setError(null)
    try {
      await guardianApi.respond(link.id, { accept })
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not answer this request'))
    }
  }

  /**
   * The parent's answer on a connection.
   *
   * <p>Approving does not connect anyone - it lets the request reach the other party, who still
   * has to accept. Worth keeping in the wording, or a parent thinks they have just introduced
   * their child to a stranger.
   */
  async function respondConnection(request: ConnectionRequestResponse, approve: boolean) {
    setError(null)
    setNotice(null)
    try {
      await connectionsApi.guardianRespond(request.id, { accept: approve })
      setNotice(
        approve
          ? `Approved. ${request.counterpartyFullName ?? 'They'} can now see the request and decide.`
          : 'Declined. Nothing was shared, and the request is closed.',
      )
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not answer this connection request'))
    }
  }

  async function revoke(childId: string) {
    if (!confirm('Remove this link? Your child keeps their account.')) return
    setError(null)
    try {
      await guardianApi.revoke(childId)
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove this link'))
    }
  }

  return (
    <div>
      <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
        <HeartHandshake size={18} className="text-sun-500" /> My children
      </h1>
      <p className="text-sm text-ink-500">
        You approve every connection, and you see what they see. Your child can see that you are linked.
      </p>

      <div className="mt-4 space-y-3">
        <ErrorMessage message={error} />
        {notice && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {notice}
          </div>
        )}

        {waitingOnMe.map((link) => (
          <div key={link.id} className="card-pad border-sun-400/60 bg-sun-400/10">
            <p className="text-sm font-semibold text-ink-900">
              {link.childName} asked you to link to their account as their{' '}
              {(link.relation ?? 'guardian').toLowerCase()}.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => respond(link, true)} className="btn-sun !px-3 !py-1.5 text-xs">Approve</button>
              <button onClick={() => respond(link, false)} className="btn-ghost !px-3 !py-1.5 text-xs">Decline</button>
            </div>
          </div>
        ))}

        {connQueue.length > 0 && (
          <div className="space-y-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">
              Connection requests needing your approval
            </div>
            {connQueue.map((r) => (
              <div key={r.id} className="card-pad border-sun-400/60 bg-sun-400/10">
                <p className="text-sm text-ink-900">
                  <strong>{r.counterpartyFullName ?? 'Someone'}</strong>
                  {r.senderParticipantId === r.minorParticipantId ? ' was asked to connect by ' : ' wants to connect with '}
                  <strong>{r.minorFullName ?? 'your child'}</strong>.
                </p>
                {r.aboutCategory && (
                  <p className="mt-1 text-xs text-ink-500">About: {r.aboutCategory}</p>
                )}
                {/* The decision needs something to decide on. Before this the card said only
                    "Someone wants to connect", and a parent had to answer for a person they knew
                    nothing about. */}
                {r.provider && <ProviderDecisionCard provider={r.provider} />}
                <p className="mt-1.5 text-xs text-ink-500">
                  Names and contact details stay hidden until everyone has agreed. Approving only
                  lets them see the request - they still have to accept.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => respondConnection(r, true)}
                    className="btn-sun !px-3 !py-1.5 text-xs"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => respondConnection(r, false)}
                    className="btn-ghost !px-3 !py-1.5 text-xs"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="card-pad text-sm text-ink-500">Loading...</div>
        ) : mine.length === 0 ? (
          <div className="card-pad text-sm text-ink-600">
            No children linked yet. If your child registered with your mobile number, their request
            appears above for you to approve. If they already have an account, send a link request
            below. Otherwise, add them.
          </div>
        ) : (
          mine.map((link) =>
            trailFor === link.childParticipantId ? (
              <ChildActivityTrail
                key={link.id}
                childId={link.childParticipantId}
                childName={link.childName ?? 'Your child'}
                onClose={() => setTrailFor(null)}
              />
            ) : (
              <div key={link.id} className="card-pad flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="break-words font-semibold text-ink-900">{link.childName ?? 'Request sent'}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                    <span className={`pill ${link.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-600'}`}>
                      {link.status === 'ACTIVE' ? 'Linked' : 'Waiting for them to approve'}
                    </span>
                    <span>{link.relation}</span>
                  </div>
                </div>
                {link.status === 'ACTIVE' && (
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {/* Only on an active link: the trail is a read of connections the parent
                        approved, and before the link is active there are none to show. */}
                    <button
                      onClick={() => setTrailFor(link.childParticipantId)}
                      className="btn-ghost !px-3 !py-1.5 text-xs gap-1.5"
                    >
                      <ListTree size={14} /> View connections
                    </button>
                    <button
                      onClick={() => revoke(link.childParticipantId)}
                      className="btn-ghost !px-3 !py-1.5 text-xs"
                    >
                      Remove link
                    </button>
                  </div>
                )}
              </div>
            ),
          )
        )}

        {open === 'link' ? (
          <LinkExistingChild
            onCancel={() => setOpen(null)}
            onDone={(message) => {
              setOpen(null)
              setNotice(message)
              load()
            }}
          />
        ) : (
          <button onClick={() => { setNotice(null); setOpen('link') }} className="card-pad flex w-full items-center gap-2 text-left text-sm font-semibold text-ink-700 transition hover:border-sun-400/60">
            <Plus size={15} /> Link a child who already has an account
          </button>
        )}

        {open === 'create' ? (
          <CreateChildAccount
            onCancel={() => setOpen(null)}
            onDone={(message) => {
              setOpen(null)
              setNotice(message)
              load()
            }}
          />
        ) : (
          <button onClick={() => { setNotice(null); setOpen('create') }} className="card-pad flex w-full items-center gap-2 text-left text-sm font-semibold text-ink-700 transition hover:border-sun-400/60">
            <Plus size={15} /> Add a new child account
          </button>
        )}
      </div>
    </div>
  )
}

function FormShell({ title, onCancel, children }: { title: string; onCancel: () => void; children: React.ReactNode }) {
  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-bold text-ink-900">{title}</h2>
        <button onClick={onCancel} aria-label="Close" className="text-ink-400 hover:text-ink-700">
          <X size={17} />
        </button>
      </div>
      {children}
    </div>
  )
}

function LinkExistingChild({ onCancel, onDone }: { onCancel: () => void; onDone: (message: string) => void }) {
  const [phone, setPhone] = useState('')
  const [relation, setRelation] = useState(RELATIONS[0])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!isValidMobile(phone)) {
      setError('Enter a valid 10-digit mobile number')
      return
    }
    setSaving(true)
    try {
      await guardianApi.linkChild({ childPhone: phone, relation })
      onDone('Request sent. Your child approves it from their own account.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the link request'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormShell title="Link a child who already has an account" onCancel={onCancel}>
      <p className="mt-1 text-xs text-ink-500">
        Your child approves the link from their own account before you can see anything.
      </p>
      <form onSubmit={submit} className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          type="tel"
          inputMode="numeric"
          pattern={MOBILE_PATTERN}
          maxLength={10}
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
          className="input sm:flex-1"
          placeholder="Child's 10-digit mobile"
          aria-label="Child's mobile number"
        />
        <select value={relation} onChange={(e) => setRelation(e.target.value)} className="input sm:w-40" aria-label="Relation">
          {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="submit" disabled={saving} className="btn-sun shrink-0">
          {saving ? 'Sending...' : 'Send link request'}
        </button>
      </form>
      <div className="mt-2"><ErrorMessage message={error} /></div>
    </FormShell>
  )
}

function CreateChildAccount({ onCancel, onDone }: { onCancel: () => void; onDone: (message: string) => void }) {
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [dob, setDob] = useState('')
  const [relation, setRelation] = useState(RELATIONS[0])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!fullName.trim()) {
      setError("Enter your child's name")
      return
    }
    if (!isValidMobile(phone)) {
      setError('Enter a valid 10-digit mobile number')
      return
    }
    if (!email.trim()) {
      setError("Enter your child's email - their join code goes there")
      return
    }
    setSaving(true)
    try {
      await guardianApi.createChildAccount({
        fullName: fullName.trim(),
        phone,
        email: email.trim(),
        dob: dob || undefined,
        relation,
      })
      onDone(`${fullName.trim()} now has an account, linked to you.`)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create the account'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormShell title="Add a new child account" onCancel={onCancel}>
      <p className="mt-1 text-xs text-ink-500">
        Creates a learner account already linked to you. Your child signs in with a join code sent
        to their email.
      </p>
      <form onSubmit={submit} className="mt-3 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label label-required">Child&apos;s full name</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="input" />
          </label>
          <label className="block">
            <span className="label label-required">Mobile number</span>
            <input
              type="tel"
              inputMode="numeric"
              pattern={MOBILE_PATTERN}
              maxLength={10}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="input"
            />
          </label>
          <label className="block">
            <span className="label label-required">Email</span>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
          </label>
          <div className="block">
            <label className="label" htmlFor="child-dob">Date of birth</label>
            <DateField
              id="child-dob"
              value={dob}
              onChange={setDob}
              min={DOB_MIN}
              max={DOB_MAX}
              openAt={DOB_OPEN_AT}
              placeholder="Select their date of birth"
            />
          </div>
          <label className="block">
            <span className="label">You are their</span>
            <select value={relation} onChange={(e) => setRelation(e.target.value)} className="input">
              {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        </div>
        <ErrorMessage message={error} />
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={saving} className="btn-sun">
            {saving ? 'Creating...' : 'Create account'}
          </button>
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </FormShell>
  )
}
